import Vue from 'vue';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { get } from 'lodash';
import i18n from '../locales/i18n';
import * as rules from 'vee-validate/dist/rules';

Vue.set(Vue.prototype, '_', { get });

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

// Custom Validators
Object.keys(rules).forEach((rule) => {
  extend(rule, rules[rule]);
});

//CUSTOM REGEX VALIDATORS
const REGEXP_VALIDATORS = {
  phoneNumber: /\+?(\d)?[ .-]?(\(?\d{3}\)?)[ .-]?(\d{3})[ .-]?(\d{4})/,
  allowedSpecialChar: /^[\w\s\-']+$/,
  breedAllowedChars: /^[a-zA-Z\s()+-]+$/,
};

Object.entries(REGEXP_VALIDATORS).forEach(([validation_key, validation_exp]) => {
  extend(validation_key, {
    validate: (value) => validation_exp.test(value),
    message: (field) => `The ${field} is not valid`,
  });
});

extend('petNameExists', {
  params: ['existingPetNames'],
  validate: (value, { existingPetNames }) => !existingPetNames.map((name) => name.toLowerCase()).includes(value.toLowerCase()),
  message: 'You can only have one pet with the same name! Please enter a unique pet name.',
});

extend('inactivePetNames', {
  params: ['inactivePetNames', 'customerSupportPhone'],
  validate: (value, { inactivePetNames }) => !inactivePetNames.map((name) => name.toLowerCase()).includes(value.toLowerCase()),
  message: (value, params) =>
    `A previously removed pet with this same name is associated with your account. If you would like to use this name for a new pet, get in touch with our customer care team at ${params.customerSupportPhone} or via email at info@docupet.com.`,
});

extend('duplicatePet', {
  params: ['name', 'message'],
  validate: (value, { name }) => value.toLowerCase() !== name.toLowerCase(),
  message: (value, { message }) => message,
});

extend('noEmojis', {
  validate: (value) =>
    !value.match(
      /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g
    ),

  message: 'Sorry, but this field does not accept emojis.',
});

extend('before', {
  params: ['valid'],
  validate: (value, { valid }) => valid,
  message: 'Select a tag style and size before entering text',
});

extend('phoneNumber', {
  message: 'Please enter your ten digit phone number',
});

//OVERRIDE REQUIRED FIELDS WITH CUSTOM MESSAGES
const getCustomMessages = (field) => {
  let message = '';
  switch (field) {
    case 'pet-species':
      message = i18n.t('dp.petPanel.petSpeciesRequired');
      break;
    case 'pet-name':
      message = i18n.t('dp.petPanel.petNameRequired');
      break;
    case 'breed-known':
      message = i18n.t('dp.input.breedKnown.customErrorMessage.required');
      break;
    case 'pet-breed':
      message = i18n.t('dp.input.breed.customErrorMessage.required');
      break;
    case 'pet-sex':
      message = i18n.t('dp.petPanel.petSexRequired');
      break;
    case 'pet-age':
      message = i18n.t('dp.petPanel.petAgeRequired');
      break;
    case 'birthdate-approximate':
      message = i18n.t('dp.petPanel.petApproximateBirthdateRequired');
      break;
    case 'color-picker':
      message = i18n.t('dp.petPanel.petColorRequired');
      break;
    case 'address-textarea':
      message = i18n.t('dp.input.addressSearch.customErrorMessage');
      break;
    case 'jurisdiction':
      message = 'Your jurisdiction is required';
      break;
    case 'phone-number':
    case 'phone':
      message = i18n.t('dp.input.phoneNumber.customErrorMessage.isEmpty');
      break;
    case 'address-civic-input':
      message = i18n.t('dp.input.civic.customErrorMessage');
      break;
    case 'address-street-input':
      message = i18n.t('dp.input.street.customErrorMessage');
      break;
    case 'address-city-input':
      message = i18n.t('dp.input.city.customErrorMessage');
      break;
    case 'address-state-input':
      message = i18n.t('dp.input.state.customErrorMessage');
      break;
    case 'address-province-input':
      message = i18n.t('dp.input.province.customErrorMessage');
      break;
    case 'address-postal-code-input':
      message = i18n.t('dp.input.postalCode.customErrorMessage');
      break;
    case 'address-zip-code-input':
      message = i18n.t('dp.input.zipCode.customErrorMessage');
      break;
    case 'month':
      message = i18n.t('dp.input.birthday.customErrorMessage.month.isEmpty');
      break;
    case 'day':
      message = i18n.t('dp.input.birthday.customErrorMessage.day.isEmpty');
      break;
    case 'year':
      message = i18n.t('dp.input.birthday.customErrorMessage.year.isEmpty');
      break;
    case 'admin-contact-first':
      message = i18n.t('errorMessages.adminFirstName');
      break;
    case 'admin-contact-last':
      message = i18n.t('errorMessages.adminLastName');
      break;
    case 'relation':
      message = i18n.t('errorMessages.adminRelation');
      break;
    default:
      message = i18n.t('errorMessages.defaultRequired', { field: field.split('-').join(' ') });
  }
  return message;
};

extend('required', {
  message: getCustomMessages,
});

extend('size', {
  message: () => i18n.t('errorMessages.incorrectFileSize'),
});

//OVERRIDE BETWEEN MESSAGE
extend('between', {
  message: (value, params) => i18n.t('dp.input.dates.errors.between', { minNum: params.min, maxNum: params.max }),
});

//OVERRIDE MIN/MAX MESSAGES
extend('min', {
  message: (value, params) => i18n.t('dp.input.dates.errors.min', { numLimit: params.length, field: params._field_ }),
});
extend('max', {
  message: (value, params) => i18n.t('dp.input.dates.errors.max', { numLimit: params.length, field: params._field_ }),
});
extend('allowedSpecialChar', {
  message: (value, params) => i18n.t('dp.errorMessages.invalidCharacters', { field: params._field_.split('-').join(' ') }),
});

//OVERRIDE BREED VALIDATION MESSAGE
extend('breedAllowedChars', {
  message: i18n.t('dp.input.breed.customErrorMessage.breedAllowedChars'),
});

//CUSTOM MINMAX NUMERIC MESSAGE
extend('minMaxNumeric', {
  params: ['min', 'max'],
  validate: (value, params) => {
    const { min, max } = params;
    const valueStringLength = value?.toString().length;
    if (valueStringLength < Number(min) || (valueStringLength === Number(min) && isNaN(value))) {
      return i18n.t('dp.input.dates.errors.minNumeric', { numLimit: min });
    }
    if (valueStringLength > Number(max) || (valueStringLength === Number(max) && isNaN(value))) {
      return i18n.t('dp.input.dates.errors.maxNumeric', { numLimit: max });
    }
    return true;
  },
});
